import ImprovedAnalyticsService from './ImprovedAnalyticsService';
import sessionDeduplicationService from './SessionDeduplicationService';
import realTimeAnalytics from './RealTimeAnalyticsAggregator';

class AnalyticsManager {
  constructor() {
    this.isInitialized = false;
    this.services = {
      analytics: ImprovedAnalyticsService,
      deduplication: sessionDeduplicationService,
      realTime: realTimeAnalytics
    };
  }

  // Initialize all analytics services
  async init() {
    if (this.isInitialized) {
      return;
    }

    try {
      // Initialize improved analytics service
      await this.services.analytics.initAnalytics();

      // Initialize real-time analytics aggregator
      await this.services.realTime.init();

      // Start session deduplication service
      this.services.deduplication.start();

      this.isInitialized = true;

    } catch (error) {
      throw error;
    }
  }

  // Track page view using the improved service
  async trackPageView(pageName, pageParams = {}) {
    try {
      if (!this.isInitialized) {
        await this.init();
      }

      await this.services.analytics.trackPageView(pageName, pageParams);
    } catch (error) {
      // Silent fail for page view tracking
    }
  }

  // Track user signup
  async trackUserSignup(userId, userData) {
    try {
      if (!this.isInitialized) {
        await this.init();
      }

      await this.services.analytics.trackUserSignup(userId, userData);
    } catch (error) {
      // Silent fail for user signup tracking
    }
  }

  // Track registry item events
  async trackRegistryItem(eventType, itemData, userId = null) {
    try {
      if (!this.isInitialized) {
        await this.init();
      }

      await this.services.analytics.trackRegistryItem(eventType, itemData, userId);
    } catch (error) {
      // Silent fail for registry item tracking
    }
  }

  // Get analytics data using the improved service
  getAnalyticsData() {
    return this.services.analytics.getAnalyticsData;
  }

  // Get real-time analytics data
  getRealTimeData() {
    return this.services.realTime.getCurrentData();
  }

  // Subscribe to real-time updates
  subscribeToRealTime(callback) {
    if (!this.isInitialized) {
      return () => {};
    }

    return this.services.realTime.subscribe(callback);
  }

  // Get deduplication statistics
  async getDeduplicationStats() {
    try {
      return await this.services.deduplication.getDeduplicationStats();
    } catch (error) {
      return null;
    }
  }

  // Force a deduplication run
  async runDeduplication() {
    try {
      await this.services.deduplication.deduplicateSessions();
    } catch (error) {
      // Silent fail for deduplication
    }
  }

  // Get comprehensive analytics health status
  async getHealthStatus() {
    try {
      const deduplicationStats = await this.getDeduplicationStats();
      const realTimeData = this.getRealTimeData();

      return {
        isInitialized: this.isInitialized,
        services: {
          analytics: true, // Always true if manager is initialized
          realTime: realTimeData.lastUpdated ? true : false,
          deduplication: deduplicationStats ? true : false
        },
        stats: {
          ...deduplicationStats,
          realTimeLastUpdate: realTimeData.lastUpdated
        },
        timestamp: new Date()
      };
    } catch (error) {
      
      return {
        isInitialized: this.isInitialized,
        services: {
          analytics: false,
          realTime: false,
          deduplication: false
        },
        error: error.message,
        timestamp: new Date()
      };
    }
  }

  // Cleanup all services
  cleanup() {
    try {
      

      // Stop session deduplication
      this.services.deduplication.stop();
      
      // Cleanup real-time analytics
      this.services.realTime.cleanup();
      
      // Cleanup improved analytics service
      if (this.services.analytics.cleanup) {
        this.services.analytics.cleanup();
      }

      this.isInitialized = false;
      
    } catch (error) {
      
    }
  }

  // Force refresh of all analytics data
  async refreshAll() {
    try {
      

      // Run deduplication
      await this.runDeduplication();

      // Update real-time aggregated data
      await this.services.realTime.updateAggregatedData();

      
    } catch (error) {
      
    }
  }
}

// Export singleton instance
export const analyticsManager = new AnalyticsManager();
export default analyticsManager;